import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { usePromocodesStore } from './store'
import { usePromocodeCreate } from '../api/create.hook'
import { usePromocodesApi } from '../api/list.hook'
import { toDiscountOptions, usePromocodeOptions } from './promocode-options'

const DEFAULT_STATUS = 'ACTIVE'

interface CreatePromocodeReturn {
  isOpen: ComputedRef<boolean>
  refStatus: Ref<string>
  refDiscount: Ref<number | undefined>
  refExpiresAt: Ref<string>
  refName: Ref<string>
  statusOptions: ComputedRef<AppSelectOption[]>
  discountOptions: ComputedRef<AppSelectOption<number>[]>
  isSaving: ComputedRef<boolean>
  isSaveDisabled: ComputedRef<boolean>
  save: () => Promise<void>
  cancel: () => void
}

export const useCreatePromocode = (): CreatePromocodeReturn => {
  const store = usePromocodesStore()
  const dayjs = useDayjs()
  const { statusOptions } = usePromocodeOptions()

  const isOpen = computed<boolean>(() => store.isModalCreate)
  const discountOptions = computed<AppSelectOption<number>[]>(() => toDiscountOptions(store.getDiscounts))

  const refStatus = ref<string>(DEFAULT_STATUS)
  const refDiscount = ref<number | undefined>(undefined)
  const refExpiresAt = ref<string>('')
  const refName = ref<string>('')

  const isSaving = computed<boolean>(() => store.isSaving)
  const isSaveDisabled = computed<boolean>(() => refDiscount.value === undefined)

  const reset = (): void => {
    refStatus.value = DEFAULT_STATUS
    refDiscount.value = store.getDiscounts.length ? Number(store.getDiscounts[0]) : undefined
    refExpiresAt.value = ''
    refName.value = ''
  }

  const cancel = (): void => {
    store.setModalCreate(false)
  }

  const save = async (): Promise<void> => {
    if (refDiscount.value === undefined) return

    const created = await usePromocodeCreate({
      discount: refDiscount.value,
      status: refStatus.value,
      expires_at: refExpiresAt.value ? dayjs(refExpiresAt.value).endOf('day').format(DateFormat.Default) : null,
      name: refName.value.trim() || null,
    })

    if (created) {
      store.setModalCreate(false)
      await usePromocodesApi()
    }
  }

  // Start each open with defaults (first available discount preselected).
  watch(isOpen, (open) => {
    if (open) reset()
  })

  return {
    isOpen,
    refStatus,
    refDiscount,
    refExpiresAt,
    refName,
    statusOptions,
    discountOptions,
    isSaving,
    isSaveDisabled,
    save,
    cancel,
  }
}
